import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthLayout from '@/features/auth/AuthLayout';
import LogoHeader from '@/components/LogoHeader';
import AuthCard from '@/features/auth/AuthCard';
import RegistrationForm from '@/features/auth/registration/RegistrationForm';
import RegistrationSuccess from '@/features/auth/registration/RegistrationSuccess';
import ConfirmEmailForm from '@/features/auth/registration/ConfirmEmailForm';
import { RegistrationFormData } from '@/features/auth/registration/schema';
import { useRegistration } from '@/hooks/useRegistration';
import { useInvitation } from '@/hooks/useInvitation';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const InvitationSignup: React.FC = () => {
  const { t } = useTranslation();
  const { InvitationData, error, loading, token } = useInvitation();

  const {
    signUp,
    confirmCode,
    resendCode,
    step,
    submittedEmail,
    approved,
    resendCooldown,
    isLoading: isAuthLoading,
    error: authError,
    clearError: clearAuthError,
  } = useRegistration({ invitationToken: token });

  const handleSubmit = async (data: RegistrationFormData) => {
    await signUp(data);
  };

  const renderContent = () => {
    if (step === 'confirm_code') {
      return (
        <>
          <ConfirmEmailForm
            email={submittedEmail}
            onSubmit={confirmCode}
            onResend={resendCode}
            resendCooldown={resendCooldown}
            error={authError}
            isLoading={isAuthLoading}
            onClearError={clearAuthError}
          />
          <p className="mt-4 text-center text-sm text-gray-600">
            <Link
              to={`/confirm-registration?email=${encodeURIComponent(submittedEmail)}`}
              className="font-medium text-blue-700 hover:underline"
            >
              {t('auth.confirm_email.confirm_later')}
            </Link>
          </p>
        </>
      );
    }

    if (step === 'success') {
      return <RegistrationSuccess approved={approved} />;
    }

    if (loading) {
      return <p className="text-center">Loading invitation...</p>;
    }

    if (error || !InvitationData) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error ||
              'There was a problem loading the invitation. Please check the link and try again.'}
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <RegistrationForm
        onSubmit={handleSubmit}
        error={authError}
        isLoading={isAuthLoading}
        onClearError={clearAuthError}
        email={InvitationData.email}
        isInvite
      />
    );
  };

  return (
    <main>
      <AuthLayout>
        <LogoHeader />
        <AuthCard>{renderContent()}</AuthCard>
      </AuthLayout>
    </main>
  );
};

export default InvitationSignup;
